import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
  Paper,
  Container,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Card,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { fetchFundsViewV2, payoutTransactions } from "../../services/backendAPI";
import { DEPOSIT_URL } from "../../utils/constant";
import { useSelector } from "react-redux";
import { fetchFundDetails, fetchPayoutTransactions, fetchFundsView } from "../../store/actions/funds";
import { useDispatch } from "react-redux";
import TransactionView from './TransactionView';
import { getValueColor } from '../../utils/utilfunction';
import useTouchInteraction from '../../hooks/useTouchInteraction';
import { ContactlessOutlined } from "@mui/icons-material";
import WithdrawModal from "../Profile/WithdrawModal";
import WithdrawButton from './WithdrawButton';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import useBalance from "../Portfolio/Positions/useBalance";
import { updateCalculatedFunds } from "../../store/actionCreators/fundsActions";
import PayIn from '../PayIn/PayIn';

// Add TouchableTab component for better touch handling
const TouchableTab = React.memo(({ label, value, selected, onChange }) => {
  const handleClick = useCallback((e) => {
    if (!e) return;
    onChange(e, value);
  }, [onChange, value]);

  const { handlers, touchStates } = useTouchInteraction(handleClick, true);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  return (
    <Tab
      {...handlers}
      label={label}
      value={value}
      onClick={(e) => handleClick(e)}
      sx={{
        minHeight: "48px",
        height: "48px",
        textTransform: "none",
        fontWeight: selected ? 600 : 400,
        whiteSpace: "nowrap",
        fontSize: "0.875rem",
        minWidth: "auto",
        flex: 1,
        opacity: 1,
        color: selected ? 'primary.main' : 'text.secondary',
        transition: 'background-color 0.2s ease, color 0.2s ease',
        backgroundColor: touchStates.isActive && !touchStates.isTouchMoved ? 'action.hover' : 'transparent',
        WebkitTapHighlightColor: 'transparent',
        '&:active': {
          backgroundColor: !touchStates.isTouchMoved ? 'action.hover' : 'transparent'
        },
        '&.MuiTab-root': {
          flex: 1,
          maxWidth: 'none',
          padding: '0 16px'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: selected ? 'primary.main' : 'transparent',
          transition: 'background-color 0.2s ease'
        }
      }}
    />
  );
});

const Funds = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Use the global balance hook instead of local calculations
  const balanceData = useBalance();
  
  // Get position metrics from Redux store
  const positionMetrics = useSelector(state => state.portfolio.positionMetrics || {});
  
  // Format currency for display
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    
    const value = parseFloat(amount);
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    
    if (absValue >= 10000000) {
      return `${sign}₹${(absValue / 10000000).toFixed(2)} Cr`;
    } else if (absValue >= 100000) {
      return `${sign}₹${(absValue / 100000).toFixed(2)} L`;
    } else if (absValue >= 1000) {
      return `${sign}₹${(absValue / 1000).toFixed(2)} K`;
    }
    
    return `${sign}₹${absValue.toFixed(2)}`;
  };

  // Get color based on profit/loss
  const getPnlColor = (value) => {
    if (!value || value === 0) return theme.palette.text.secondary;
    return value > 0 ? theme.palette.success.main : theme.palette.error.main;
  };
  
  const { userData, userSettings } = useSelector(state => state.profile);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedFundType, setFundType] = useState("ALL");
  const [funds, setFundDetails] = useState([]);
  const fundDetails = useSelector((state) => state.funds.fundDetails);
  const payoutTransactions = useSelector((state) => state.funds.payoutTransactions);
  const availableFunds = useSelector((state) => state.funds.fundsViewV2);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState(1);

  useEffect(() => {
    dispatch(fetchFundsView());
  }, []);
  
  // Add touch interaction for Add Funds button
  const handleAddFundsClick = useCallback(() => {
    if (!userData?.phone_number) {
      console.warn('User data or phone number not available');
      return;
    }
    navigate("/payin", {replace: true})
  }, [userData]);

  const { handlers: addFundsHandlers, touchStates: addFundsTouchStates } = useTouchInteraction(handleAddFundsClick, true);
  
  useEffect(() => {
    const client_id = localStorage.getItem("login_id");
    // Refresh the calculated funds data
    dispatch(updateCalculatedFunds());
    dispatch(fetchPayoutTransactions({user_id:client_id,client_id:client_id,page_no:pageNo,page_size:100,first_page:pageNo===1,last_page:false,user_type:"CLIENT"}));
  }, [dispatch, pageNo]);

  useEffect(() => {
    setFundDetails(fundDetails);
  }, [fundDetails]);

  const handleTabChange = useCallback((event, newValue) => {
    setSelectedTab(newValue);
  }, []);

  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isAddFundsModalOpen, setIsAddFundsModalOpen] = useState(false);

  // Create a formatted object from balanceData to display in UI
  const createDisplayFundsObject = useCallback(() => {
    const allFunds = {
      "Balance": balanceData.formatted.tradingBalance,
      "Available Margin": balanceData.formatted.availableCash,
      "Unrealized P&L": balanceData.formatted.unrealizedPnl,
      // "Total P&L": balanceData.formatted.totalPnl,
      // "Realized P&L": balanceData.formatted.realizedPnl,
      "Blocked Margin": balanceData.formatted.blockedMargin,
      "Equity": balanceData.formatted.equity,
      "Credit": balanceData.formatted.credit
    };
    // Always show Unrealized P&L and Blocked Margin, filter Credit if zero
    return Object.fromEntries(
      Object.entries(allFunds).filter(([key, value]) => {
        if (key === 'Credit') {
          return parseFloat(value?.toString().replace(/[^\d.-]/g, '')) !== 0;
        }
        // Show all other fields, even if zero
        return key !== undefined;
      })
    );
  }, [balanceData]);

  // Get the funds data to display
  const displayFunds = createDisplayFundsObject();

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ 
        mb: 4,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}>
        <Box sx={{ flex: 1, minWidth: '200px' }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 0.5,
              color: theme.palette.text.primary,
              fontSize: { xs: '1.5rem', sm: '1.75rem' }
            }}
          >
            Funds
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Manage your trading account funds
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          alignItems: 'center'
        }}>
          <Button
            variant="contained"
            onClick={() => setIsWithdrawModalOpen(true)}
            sx={{
              borderRadius: 2,
              py: { xs: 1, sm: 1.5 },
              px: { xs: 2, sm: 3 },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              boxShadow: `0 4px 12px ${theme.palette.success.main}20`,
              background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: { xs: '140px', sm: '160px' },
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                boxShadow: `0 6px 16px ${theme.palette.success.main}30`,
                transform: 'translateY(-1px)'
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: `0 4px 12px ${theme.palette.success.main}20`,
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Box component="span" sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1 
            }}>
              <AccountBalanceWalletIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              <span>Withdraw</span>
            </Box>
          </Button>
          <Button
            variant="contained"
            onClick={() => setIsAddFundsModalOpen(true)}
            sx={{
              borderRadius: 2,
              py: { xs: 1, sm: 1.5 },
              px: { xs: 2, sm: 3 },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: { xs: '140px', sm: '160px' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: theme.palette.primary.main,
              '&:hover': {
                background: theme.palette.primary.dark,
                transform: 'translateY(-1px)'
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Box component="span" sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1 
            }}>
              <AddIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              <span>Add Funds</span>
            </Box>
          </Button>
        </Box>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 2,
          position: 'relative',
          border: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.down('sm')]: {
            position: 'sticky',
            top: 0,
            zIndex: 1200,
            backgroundColor: 'background.paper'
          }
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            boxShadow: "none",
            minHeight: '48px',
            "& .MuiTabs-flexContainer": {
              height: "48px",
              width: '100%',
              display: 'flex'
            },
            "& .MuiTabs-scroller": {
              width: '100%'
            },
            "& .MuiTabs-indicator": {
              height: '3px',
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <TouchableTab
            label="Info"
            value={0}
            selected={selectedTab === 0}
            onChange={handleTabChange}
          />
          <TouchableTab
            label="Transactions"
            value={1}
            selected={selectedTab === 1}
            onChange={handleTabChange}
          />
        </Tabs>
      </Paper>

      <Box sx={{ 
        flex: 1,
        height: isMobile ? 'calc(100% - 48px)' : 'calc(100vh - 48px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        WebkitOverflowScrolling: 'touch',
      }}>
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'none',
          p: { xs: 0.5, sm: 1 },
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          pb: isMobile ? '56px' : 0
        }}>
          {selectedTab === 0 && (
            <>
              <List sx={{
                '& .MuiListItem-root': {
                  px: isMobile ? 1 : 2,
                  py: isMobile ? 0.75 : 1
                },
                '& .MuiTypography-root': {
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }
              }}>
                {/* Display all funds values from the global balance hook */}
                {Object.entries(displayFunds).map(([name, value], index) => (
                  <React.Fragment key={`updated-${index}`}>
                    <ListItem>
                      <ListItemText 
                        primary={name} 
                        sx={{ 
                          flex: '1 1 50%',
                          '& .MuiTypography-root': {
                            fontWeight: 700 // Make label bold
                          } 
                        }}
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, // Increase weight from 500 to 600
                          color: name.includes('P&L') ? 
                            balanceData.isPositive[name.toLowerCase().includes('unrealized') ? 'unrealizedPnl' : 
                                                 name.toLowerCase().includes('realized') ? 'realizedPnl' : 'totalPnl'] ? 
                              theme.palette.success.main : theme.palette.error.main
                            : parseFloat(value.replace(/[^\d.-]/g, '')) < 0 ? 
                              theme.palette.error.main : 
                              parseFloat(value.replace(/[^\d.-]/g, '')) > 0 ? 
                              theme.palette.success.main : theme.palette.text.primary
                        }}
                      >
                        {value}
                      </Typography>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </>
          )}

          {selectedTab === 1 && (
            <TransactionView payoutTransactions={payoutTransactions} pageNo={pageNo} setPageNo={setPageNo}/>
          )}
        </Box>
      </Box>
      <WithdrawModal open={isWithdrawModalOpen} onClose={() => setIsWithdrawModalOpen(false)} />
      <Dialog
        open={isAddFundsModalOpen}
        onClose={() => setIsAddFundsModalOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: 'hidden',
            p: 0,
            maxWidth: 500,
            width: '100%',
            boxShadow: 24,
            m: { xs: 1, sm: 3 },
            background: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
          }
        }}
      >
        {/* Custom Modal Header */}
        <Box
          sx={{
            background: theme => theme.palette.primary.main,
            color: 'white',
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1.5, sm: 2.5 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <Box>
            <Box sx={{ fontWeight: 700, fontSize: '1.35rem', mb: 0.5 }}>Add Funds</Box>
            <Box sx={{ fontSize: '0.98rem', opacity: 0.85 }}>Add money to your trading account</Box>
          </Box>
          <IconButton
            onClick={() => setIsAddFundsModalOpen(false)}
            sx={{ color: 'white', ml: 2, background: 'rgba(255,255,255,0.08)', '&:hover': { background: 'rgba(255,255,255,0.18)' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Scrollable PayIn content */}
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <PayIn onClose={() => setIsAddFundsModalOpen(false)} isModal={true} />
        </Box>
      </Dialog>
    </Container>
  );
};

export default Funds;